import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryClient';
import {
  listFolders,
  getFolder,
  createFolder,
  updateFolder,
  deleteFolder,
  type CreateFolderData,
  type UpdateFolderData,
} from '../../services/diagramService';
import type { Folder } from '../../types';

/**
 * Query hook for fetching all folders
 */
export function useFoldersQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.folders.list(),
    queryFn: () => listFolders(true), // Always bypass browser cache
    enabled,
    staleTime: 60 * 1000, // 1 minute - folders change less frequently
  });
}

/**
 * Query hook for fetching a single folder
 */
export function useFolderQuery(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.folders.detail(id!),
    queryFn: () => getFolder(id!, true), // Always bypass browser cache
    enabled: enabled && !!id,
    staleTime: 60 * 1000,
  });
}

/**
 * Mutation hook for creating a folder
 */
export function useCreateFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFolderData) => createFolder(data),
    onSuccess: (newFolder) => {
      // Optimistically add to list
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.list(),
        (old) => old ? [newFolder, ...old] : [newFolder]
      );
    },
  });
}

/**
 * Mutation hook for updating a folder
 */
export function useUpdateFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFolderData }) =>
      updateFolder(id, data),
    onSuccess: (updatedFolder) => {
      // Update in list
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.list(),
        (old) => old?.map((f) => (f.id === updatedFolder.id ? updatedFolder : f))
      );
      // Update detail cache
      queryClient.setQueryData(
        queryKeys.folders.detail(updatedFolder.id),
        updatedFolder
      );
    },
  });
}

/**
 * Mutation hook for deleting a folder
 */
export function useDeleteFolderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: (_data, id) => {
      // Remove from list
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.list(),
        (old) => old?.filter((f) => f.id !== id)
      );
      // Remove detail cache
      queryClient.removeQueries({ queryKey: queryKeys.folders.detail(id) });
      // Invalidate diagrams list (folder assignments may have changed)
      queryClient.invalidateQueries({ queryKey: queryKeys.diagrams.list() });
    },
  });
}

/**
 * Helper for optimistic folder updates
 */
export function useOptimisticFolderUpdate() {
  const queryClient = useQueryClient();

  return {
    updateFolderInList: (id: string, updates: Partial<Folder>) => {
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.list(),
        (old) => old?.map((f) => (f.id === id ? { ...f, ...updates } : f))
      );
    },
    removeFolderFromList: (id: string) => {
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.list(),
        (old) => old?.filter((f) => f.id !== id)
      );
    },
  };
}
